import { lookupInsurance } from './coventryInsuranceLookup';
import { getMonthlyPMIAmount } from '../../../utils/mortgage/pmiRates';

/**
 * Calculate monthly PMI (Private Mortgage Insurance) based on loan amount and credit score.
 * Delegates to the canonical PMI rate matrix in utils/mortgage/pmiRates.ts.
 */
export const calculateMonthlyPMI = (
  loanAmount: number,
  downPaymentPercentage: number,
  creditScore: number,
  loanType: "conventional" | "fha" | "va" | "usda"
): number => {
  return getMonthlyPMIAmount(loanAmount, loanType, creditScore, downPaymentPercentage);
};

/**
 * Calculate monthly homeowner's insurance based on more accurate rate
 * Supports Coventry community-specific rates when available
 */
export const calculateMonthlyInsurance = (
  homePrice: number,
  annualInsurance: number,
  community?: string,
  model?: string,
  sqft?: number
): number => {
  // If annual insurance is explicitly provided, use that
  if (annualInsurance > 0) {
    return annualInsurance / 12;
  }
  
  // Try Coventry lookup if community/model provided
  if (community || sqft) {
    const result = lookupInsurance(community, model, sqft);
    if (result.annualPremium > 0) {
      return result.monthlyPremium;
    }
  }
  
  // Fallback: 0.42% of home price annually (based on worksheet analysis)
  const estimatedAnnualInsurance = homePrice * 0.0042;
  return estimatedAnnualInsurance / 12;
};

/**
 * Get default annual insurance amount
 * Uses Coventry rates when community/model specified, otherwise estimates
 */
export const getDefaultAnnualInsurance = (
  homePrice: number,
  community?: string,
  model?: string,
  sqft?: number
): number => {
  // Try Coventry lookup if community/model provided
  if (community || sqft) {
    const result = lookupInsurance(community, model, sqft);
    if (result.annualPremium > 0) {
      return result.annualPremium;
    }
  }
  
  // Fallback: 0.42% of home price
  return homePrice * 0.0042;
};

/**
 * Insurance source types for tracking where the rate came from
 */
export type InsuranceSource = 'manual' | 'coventry' | 'estimated';
